import { getSendableWhatsappTemplates, WhatsappTemplate } from '@/lib/api/messaging/whatsapp-templates-api'
import { useEffect, useState } from 'react'

/** Variáveis do corpo, na ordem em que aparecem (`{{1}}`, `{{nome}}`...). */
function extractTemplateVariables(bodyText: string): string[] {
    const found: string[] = []
    for (const match of bodyText.matchAll(/\{\{\s*([^}]+?)\s*\}\}/g)) {
        if (!found.includes(match[1])) found.push(match[1])
    }
    return found
}

/**
 * O template salvo no passo. O formato atual é o id local em texto (`"42"`);
 * fluxos antigos podem ter o identificador do provedor anterior, que o envio
 * ainda aceita — aqui ele só é reconhecido para aparecer selecionado.
 */
function findSavedTemplate(templates: WhatsappTemplate[], templateId?: string): WhatsappTemplate | undefined {
    if (!templateId) return undefined
    return templates.find(t => String(t.id) === templateId) ?? templates.find(t => !!t.contentSid && t.contentSid === templateId)
}

/**
 * Template enviável + um campo por variável. Grava `templateId` (id local do
 * template) e `templateParams` (lista), que é o que o motor lê. A lista vem
 * filtrada pelo canal do setor: na API oficial, só os aprovados pela Meta.
 */
export default function WhatsappTemplateConfig({
    departmentId,
    templateId,
    templateParams,
    onChange,
    size = 'xs',
}: {
    size?: 'xs' | 'sm'
    departmentId: number | null
    templateId?: string
    templateParams?: string[]
    onChange: (templateId: string | undefined, templateParams: string[]) => void
}) {
    const [templates, setTemplates] = useState<WhatsappTemplate[]>([])
    const [loaded, setLoaded] = useState(false)
    const selectClass = size === 'sm' ? 'select select-bordered select-sm w-full' : 'select select-bordered select-xs w-full'
    const inputClass = size === 'sm' ? 'input input-bordered input-sm w-full' : 'input input-bordered input-xs w-full'

    useEffect(() => {
        setLoaded(false)
        getSendableWhatsappTemplates(departmentId)
            .then(res => setTemplates(res.success && Array.isArray(res.result) ? res.result : []))
            .catch(() => setTemplates([]))
            .finally(() => setLoaded(true))
    }, [departmentId])

    const selected = findSavedTemplate(templates, templateId)
    const variables = selected ? extractTemplateVariables(selected.bodyText) : []
    const params = templateParams ?? []

    return (
        <div className="space-y-2">
            <div>
                <label className="text-xs font-medium text-base-content/70 block mb-1">Template</label>
                <select
                    className={selectClass}
                    value={selected ? String(selected.id) : templateId ?? ''}
                    onChange={e => onChange(e.target.value || undefined, [])}
                >
                    <option value="">Selecione um template</option>
                    {templateId && !selected && loaded && <option value={templateId}>Template indisponível</option>}
                    {templates.map(t => (
                        <option key={t.id} value={String(t.id)}>{t.friendlyName || t.name}</option>
                    ))}
                </select>
                {templateId && !selected && loaded && (
                    <p className="text-xs text-warning mt-1">O template salvo não está mais disponível para envio.</p>
                )}
            </div>
            {selected && (
                <p className="text-xs text-base-content/60 bg-base-200 rounded p-2 whitespace-pre-wrap leading-snug">{selected.bodyText}</p>
            )}
            {variables.map((variable, i) => (
                <div key={variable}>
                    <label className="text-xs font-medium text-base-content/70 block mb-1">Variável {`{{${variable}}}`}</label>
                    <input
                        type="text"
                        className={inputClass}
                        value={params[i] ?? ''}
                        onChange={e => {
                            const next = variables.map((_, j) => (j === i ? e.target.value : params[j] ?? ''))
                            onChange(templateId, next)
                        }}
                        placeholder="Texto ou {{lead.name}}"
                    />
                </div>
            ))}
        </div>
    )
}
